import React, { useState, useEffect } from 'react';
import { X, HelpCircle, Loader2, BookOpen, Monitor, Sprout, TrendingUp, Globe, MessageSquare, CreditCard, Calculator, Zap, Lightbulb, ArrowRight } from 'lucide-react';
import ApiService from '../services/apiService';

// ── Client-side bilingual guide content ──
const GUIDE_CONTENT = {
  dashboard: {
    en: {
      title: "IoT Dashboard",
      icon: Monitor,
      steps: [
        "Select a device from the sidebar to view real-time sensor data",
        "View historical charts by adjusting the time interval (1H, 6H, 24H, 7D)",
        "Use 'Manual Lab' to input values manually when sensors are offline",
        "Click 'Deep AI Analysis' for a comprehensive RAG-based diagnosis",
        "Weather data is automatically fetched for your device's location"
      ],
      tip: "Tip: The AI uses both sensor data and global context to provide the most accurate diagnosis."
    },
    ar: {
      title: "لوحة تحكم إنترنت الأشياء",
      icon: Monitor,
      steps: [
        "اختر جهاز من الشريط الجانبي لعرض بيانات الحساسات في الوقت الفعلي",
        "عرض الرسوم البيانية التاريخية عبر ضبط الفترة الزمنية (1س، 6س، 24س، 7ي)",
        "استخدم 'المختبر اليدوي' لإدخال القيم يدوياً عندما تكون الحساسات غير متصلة",
        "انقر 'التحليل العميق بالذكاء الاصطناعي' لتشخيص شامل قائم على RAG",
        "يتم جلب بيانات الطقس تلقائياً بناءً على موقع جهازك"
      ],
      tip: "نصيحة: يستخدم الذكاء الاصطناعي بيانات الحساسات والسياق العام لتقديم أدق تشخيص."
    }
  },
  control: {
    en: {
      title: "Farm Control & Automation",
      icon: Zap,
      steps: [
        "Toggle actuators (pumps, LEDs, valves) on/off with a single tap",
        "Create Cron schedules for time-based automation",
        "Set Threshold triggers to activate devices based on sensor readings",
        "Use 'AI Suggest' to generate optimal schedules automatically",
        "Edit or delete schedules from the schedule list"
      ],
      tip: "Tip: AI-suggested schedules analyze your IoT data and weather to recommend the best automation plan."
    },
    ar: {
      title: "التحكم والأتمتة",
      icon: Zap,
      steps: [
        "تشغيل/إيقاف المشغلات (مضخات، إضاءة، صمامات) بنقرة واحدة",
        "إنشاء جداول Cron للأتمتة الزمنية",
        "ضبط مشغلات العتبة لتنشيط الأجهزة بناءً على قراءات الحساسات",
        "استخدم 'اقتراح الذكاء الاصطناعي' لإنشاء جداول مثالية تلقائياً",
        "تعديل أو حذف الجداول من قائمة الجداول"
      ],
      tip: "نصيحة: الجداول المقترحة بالذكاء الاصطناعي تحلل بيانات IoT والطقس لتوصية بأفضل خطة أتمتة."
    }
  },
  crops: {
    en: {
      title: "Crop Recommendation",
      icon: Sprout,
      steps: [
        "Upload a soil image to identify soil type (optional)",
        "Set the Global Context bar with your crop type, season, and irrigation",
        "Enter N, P, K, temperature, humidity, and pH values",
        "Click 'Auto-Fill Sensors' to populate data from your IoT device",
        "Click 'Get Recommendations' to receive AI-powered crop suggestions"
      ],
      tip: "Tip: Soil image upload is optional — the AI can use your Global Context settings as a fallback."
    },
    ar: {
      title: "توصية المحاصيل",
      icon: Sprout,
      steps: [
        "ارفع صورة تربة لتحديد نوع التربة (اختياري)",
        "اضبط شريط السياق العام بنوع المحصول والموسم ونوع الري",
        "أدخل قيم N، P، K، الحرارة، الرطوبة، والأس الهيدروجيني",
        "انقر 'تعبئة تلقائية' لملء البيانات من جهاز IoT الخاص بك",
        "انقر 'احصل على التوصيات' للحصول على اقتراحات محاصيل بالذكاء الاصطناعي"
      ],
      tip: "نصيحة: رفع صورة التربة اختياري — يمكن للذكاء الاصطناعي استخدام إعدادات السياق العام كبديل."
    }
  },
  yield: {
    en: {
      title: "Yield Prediction",
      icon: TrendingUp,
      steps: [
        "Select the crop type from the dropdown menu",
        "Enter water availability (rainfall) and soil nutrient values",
        "Use 'Auto-Fill Sensors' to pull data from your IoT device",
        "Click 'Calculate Expected Yield' to run the ML prediction model",
        "View the predicted yield in kg/acre with AI analysis"
      ],
      tip: "Tip: The model considers both environmental factors and historical agricultural data for accurate predictions."
    },
    ar: {
      title: "توقع الحصاد",
      icon: TrendingUp,
      steps: [
        "اختر نوع المحصول من القائمة المنسدلة",
        "أدخل توافر المياه (الأمطار) وقيم العناصر الغذائية في التربة",
        "استخدم 'التعبئة التلقائية' لسحب البيانات من جهاز IoT",
        "انقر 'حساب التوقع' لتشغيل نموذج التعلم الآلي",
        "عرض الإنتاج المتوقع بالكيلوجرام/فدان مع تحليل الذكاء الاصطناعي"
      ],
      tip: "نصيحة: يراعي النموذج العوامل البيئية والبيانات الزراعية التاريخية للتنبؤات الدقيقة."
    }
  },
  satellite: {
    en: {
      title: "Satellite Intelligence",
      icon: Globe,
      steps: [
        "Click on the map or use 'My Location' to select a target area",
        "Draw a polygon for precise farm boundary analysis",
        "Set date range and cloud cover parameters",
        "Click 'Run Analysis' to fetch Sentinel-2 satellite data",
        "View NDVI, NDMI, NDWI, and other vegetation indices side by side"
      ],
      tip: "Tip: Use the comparison period to track changes in crop health over time."
    },
    ar: {
      title: "الاستخبارات الفضائية",
      icon: Globe,
      steps: [
        "انقر على الخريطة أو استخدم 'موقعي' لتحديد المنطقة المستهدفة",
        "ارسم مضلعاً لتحليل دقيق لحدود المزرعة",
        "حدد نطاق التاريخ ومعايير الغطاء السحابي",
        "انقر 'تشغيل التحليل' لجلب بيانات قمر Sentinel-2",
        "عرض مؤشرات NDVI وNDMI وNDWI ومؤشرات النبات الأخرى جنباً إلى جنب"
      ],
      tip: "نصيحة: استخدم فترة المقارنة لتتبع التغيرات في صحة المحصول بمرور الوقت."
    }
  },
  advanced_ai: {
    en: {
      title: "AI Consultant",
      icon: MessageSquare,
      steps: [
        "Type your agricultural question in the chat input",
        "Attach images (leaf, soil, crop) for visual analysis",
        "The AI streams responses in real-time with reasoning steps",
        "View chat history and resume previous conversations",
        "Responses include emoji-rich formatting with tables and actionable steps"
      ],
      tip: "Tip: You can ask complex multi-part questions — the AI retains conversation context."
    },
    ar: {
      title: "المستشار الذكي",
      icon: MessageSquare,
      steps: [
        "اكتب سؤالك الزراعي في حقل الدردشة",
        "أرفق صوراً (أوراق، تربة، محاصيل) للتحليل البصري",
        "يبث الذكاء الاصطناعي الردود في الوقت الفعلي مع خطوات التفكير",
        "عرض سجل الدردشة واستئناف المحادثات السابقة",
        "تتضمن الردود تنسيقاً غنياً بالرموز التعبيرية مع جداول وخطوات عملية"
      ],
      tip: "نصيحة: يمكنك طرح أسئلة معقدة متعددة الأجزاء — يحتفظ الذكاء الاصطناعي بسياق المحادثة."
    }
  },
  billing: {
    en: {
      title: "Billing & Credits",
      icon: CreditCard,
      steps: [
        "Choose payment method: Vodafone Cash/InstaPay or Bank Transfer",
        "Transfer the subscription amount to the provided account",
        "Take a screenshot of the completed transaction",
        "Upload the receipt in the 'Verify Transaction' section",
        "Credits are added after admin approval (usually within 24 hours)"
      ],
      tip: "Tip: Include your registered email in the bank transfer reference for faster verification."
    },
    ar: {
      title: "الفوترة والرصيد",
      icon: CreditCard,
      steps: [
        "اختر طريقة الدفع: فودافون كاش/إنستاباي أو تحويل بنكي",
        "حوّل مبلغ الاشتراك إلى الحساب المقدم",
        "التقط لقطة شاشة للمعاملة المكتملة",
        "ارفع إيصال الدفع في قسم 'التحقق من المعاملة'",
        "يُضاف الرصيد بعد موافقة الإدارة (عادةً خلال 24 ساعة)"
      ],
      tip: "نصيحة: أدرج بريدك الإلكتروني المسجل في مرجع التحويل البنكي لتسريع التحقق."
    }
  },
  accounting: {
    en: {
      title: "Farm Accounting",
      icon: Calculator,
      steps: [
        "Select a fiscal year and farm to manage",
        "Add revenue and expense nodes using the tree structure",
        "Enter amounts, quantities, and frequencies for each item",
        "View auto-calculated totals and net profit",
        "Generate an AI financial analysis report"
      ],
      tip: "Tip: Use categories to organize expenses by type (labor, fertilizer, equipment, etc.)."
    },
    ar: {
      title: "المحاسبة الزراعية",
      icon: Calculator,
      steps: [
        "اختر السنة المالية والمزرعة للإدارة",
        "أضف عقد الإيرادات والمصروفات باستخدام هيكل الشجرة",
        "أدخل المبالغ والكميات والتكرارات لكل بند",
        "عرض الإجماليات المحسوبة تلقائياً وصافي الربح",
        "إنشاء تقرير تحليل مالي بالذكاء الاصطناعي"
      ],
      tip: "نصيحة: استخدم الفئات لتنظيم المصروفات حسب النوع (عمالة، أسمدة، معدات، إلخ)."
    }
  }
};

const HelpGuideModal = ({ isOpen, onClose, activeTab, lang, isRTL }) => {
    const [guideData, setGuideData] = useState({ type: 'none', url: null });
    const [isLoading, setIsLoading] = useState(true);
    const [useLocalContent, setUseLocalContent] = useState(false);

    useEffect(() => {
        if (isOpen && activeTab) {
            setIsLoading(true);
            setUseLocalContent(false);
            const fetchGuide = async () => {
                try {
                    const response = await ApiService.get(`/support/guides/${activeTab}`);
                    if (response.status === 'success' && response.data?.type !== 'none') {
                        setGuideData(response.data);
                    } else {
                        // Fallback to local content
                        setUseLocalContent(true);
                    }
                } catch (error) {
                    console.error("Failed to fetch guide for tab:", activeTab);
                    setUseLocalContent(true);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchGuide();
        }
    }, [isOpen, activeTab]);

    if (!isOpen) return null;

    const currentLang = lang || (isRTL ? 'ar' : 'en');
    const localGuide = GUIDE_CONTENT[activeTab]?.[currentLang] || GUIDE_CONTENT[activeTab]?.en;
    const GuideIcon = localGuide?.icon || HelpCircle;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity p-4 md:p-8" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col overflow-hidden relative animate-fade-in-slide border border-[#1B263B]/10 dark:border-slate-700">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-800 bg-[#F4EFEA]/30 dark:bg-slate-800/50">
                    <h2 className="text-xl font-bold flex items-center gap-2 text-geo-navy dark:text-white capitalize">
                        <GuideIcon className="text-[#6B8F71]" size={24} />
                        {localGuide?.title || activeTab.replace('-', ' ')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-gray-500"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 min-h-[400px] max-h-[70vh] w-full bg-slate-50 dark:bg-slate-950 flex flex-col p-6 relative overflow-y-auto custom-scrollbar">

                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center flex-1 text-gray-400">
                            <Loader2 size={48} className="animate-spin mb-4 text-[#6B8F71]" />
                            <p>{isRTL ? 'جاري تحميل الدليل...' : 'Loading interactive guide...'}</p>
                        </div>
                    ) : !useLocalContent && guideData.type === 'gif' && guideData.url ? (
                        <img
                            src={guideData.url}
                            alt={`Guide for ${activeTab}`}
                            className="w-full h-full object-contain rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
                        />
                    ) : !useLocalContent && guideData.type === 'iframe' && guideData.url ? (
                        <iframe
                            src={guideData.url}
                            style={{ minHeight: '640px' }}
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            allowFullScreen="allowfullscreen"
                            className="rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
                        />
                    ) : localGuide ? (
                        <div className="space-y-6 animate-fade-in">
                            {/* Steps */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-bold uppercase tracking-wider text-[#C47A2C] flex items-center gap-2">
                                    <BookOpen size={16} />
                                    {isRTL ? 'خطوات الاستخدام' : 'How to Use'}
                                </h3>
                                {localGuide.steps.map((step, i) => (
                                    <div key={i} className="flex items-start gap-3 bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-100 dark:border-slate-800 shadow-sm transition-transform hover:-translate-y-0.5">
                                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#6B8F71]/15 text-[#6B8F71] flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </span>
                                        <p className="text-sm text-[#1B263B] dark:text-white leading-relaxed font-medium">{step}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Tip */}
                            {localGuide.tip && (
                                <div className="flex items-start gap-3 p-4 bg-[#C47A2C]/8 border border-[#C47A2C]/15 rounded-xl">
                                    <Lightbulb size={18} className="text-[#C47A2C] shrink-0 mt-0.5" />
                                    <p className="text-sm text-[#A36322] dark:text-[#C47A2C] font-medium leading-relaxed">{localGuide.tip}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center text-gray-500 dark:text-gray-400 p-8 flex flex-col items-center justify-center flex-1">
                            <HelpCircle size={64} className="mx-auto mb-4 opacity-20" />
                            <h3 className="text-lg font-medium text-geo-navy dark:text-white">
                                {isRTL ? 'لا يوجد دليل متاح' : 'No guide available'}
                            </h3>
                            <p className="mt-2">{isRTL ? 'نحن نعمل حالياً على إنشاء الشرح لهذه الميزة.' : 'We are currently creating the tutorial for this feature.'}</p>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default HelpGuideModal;